<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="QoS詳細設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <cross-validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <v-row>
                  <v-col>
                    <app-label-group
                      label="VPN/VNコード"
                      :value="
                        vpnVnCode.charAt(0) !== 'B'
                          ? `${vpnVnCode}(${vnName})`
                          : vpnVnCode
                      "
                    />
                  </v-col>
                </v-row>

                <!-- QoS方式 -->
                <v-row>
                  <v-col>
                    <validation-provider
                      name="qosType"
                      label="QoS方式"
                      :rules="{ required_value: { check: 'NONE' } }"
                      v-model="form.qosType"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-badge item="QoS方式" :isInfo="false" />
                      <app-select
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                        :items="qosTypeOptions"
                        item-title="text"
                        item-value="value"
                      >
                      </app-select>
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 優先度参照先 -->
                <v-row>
                  <v-col>
                    <validation-provider
                      name="priorityTarget"
                      label="優先度参照先"
                      :rules="{ required_value: { check: 'NONE' } }"
                      v-model="form.priorityTarget"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-badge item="優先度参照先" :isInfo="false" />
                      <app-select
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                        :items="priorityTargetOptions"
                        item-title="text"
                        item-value="value"
                      ></app-select>
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- QoS方式：絶対優先方式 -->
                <template v-if="form.qosType === 'ABSOLUTE'">
                  <h3 class="font-wight-bold mt-2">優先度割当</h3>
                  <v-row>
                    <v-col>
                      <app-badge
                        item="パターン選択"
                        :isInfo="false"
                        :isRequired="false"
                      />
                      <app-select
                        :items="absolutePatternOption"
                        v-model="absolutePattern"
                        item-title="text"
                        item-value="value"
                      ></app-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-table density="compact" class="table table-bordered">
                        <thead>
                          <tr>
                            <th colspan="2" class="text-center">
                              CoS/IP Precedence
                            </th>
                            <th
                              v-for="index in 8"
                              :key="index"
                              class="text-center"
                            >
                              {{ index - 1 }}
                            </th>
                          </tr>
                        </thead>
                        <tbody class="text-center">
                          <th :rowspan="5">優先度</th>
                          <tr
                            v-for="(
                              rowName, rowNameIndex
                            ) in absolutePriorityRowNames"
                            :key="rowNameIndex"
                          >
                            <th>
                              {{ rowName.value }}
                            </th>
                            <td
                              v-for="(
                                propName, propNameIndex
                              ) in absolutePriorityPropNames"
                              :key="propNameIndex"
                            >
                              <v-radio-group
                                v-model="
                                  form.qosAbsolute![propName as PriorityProps]
                                "
                                :disabled="
                                  propName === 'priority6' &&
                                  form.priorityTarget === 'IPPRECEDENCE'
                                "
                                hide-details
                              >
                                <v-radio
                                  :value="rowName.key"
                                  class="radio-button"
                                ></v-radio>
                              </v-radio-group>
                            </td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-col>
                  </v-row>
                </template>

                <!-- QoS方式：帯域制御方式 -->
                <template v-if="form.qosType === 'BANDWIDTH'">
                  <h3 class="font-weight-bold mb-2 mt-4">優先度割当</h3>
                  <v-table density="compact" class="table table-bordered">
                    <thead>
                      <tr>
                        <th colspan="2" rowspan="2"></th>
                        <th class="text-center" colspan="4">
                          CoS/IP Precedence
                        </th>
                      </tr>
                      <tr>
                        <th class="fixed-width">
                          <app-select
                            :items="priorityTypes"
                            v-model="qosBandwidth.priorityType1"
                            item-title="text"
                            item-value="value"
                            hide-details
                            class="table-head-select"
                          />
                        </th>
                        <th class="fixed-width">
                          <app-select
                            :items="priorityTypes"
                            v-model="qosBandwidth.priorityType2"
                            item-title="text"
                            item-value="value"
                            hide-details
                            class="table-head-select"
                          />
                        </th>
                        <th class="fixed-width">
                          <app-select
                            :items="priorityTypes"
                            v-model="qosBandwidth.priorityType3"
                            item-title="text"
                            item-value="value"
                            hide-details
                            class="table-head-select"
                          />
                        </th>
                        <th>ベストエフォート</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template
                        v-for="(rowName, rowNameIndex) in bandwidthRowNames"
                        :key="rowNameIndex"
                      >
                        <tr>
                          <template v-if="rowNameIndex % 2 === 0">
                            <th rowspan="2">
                              {{ rowName.value }}
                            </th>
                            <th>割当帯域</th>
                            <td>
                              <app-select
                                :items="bandwidthValueOptions"
                                v-model="
                                  qosBandwidth.qosBandwidthList[
                                    Math.floor(rowNameIndex / 2)
                                  ].priorityType1Value
                                "
                                item-title="text"
                                item-value="value"
                                hide-details
                              />
                            </td>
                            <td>
                              <app-select
                                :items="bandwidthValueOptions"
                                v-model="
                                  qosBandwidth.qosBandwidthList[
                                    Math.floor(rowNameIndex / 2)
                                  ].priorityType2Value
                                "
                                item-title="text"
                                item-value="value"
                                hide-details
                              />
                            </td>
                            <td>
                              <app-select
                                :items="bandwidthValueOptions"
                                v-model="
                                  qosBandwidth.qosBandwidthList[
                                    Math.floor(rowNameIndex / 2)
                                  ].priorityType3Value
                                "
                                item-title="text"
                                item-value="value"
                                hide-details
                              />
                            </td>
                            <td class="text-right">
                              <template
                                v-if="
                                  sumPriorityTypeValue(
                                    qosBandwidth.qosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ]
                                  ) > 90
                                "
                              >
                                <span class="text-red">OVER</span>
                              </template>
                              <template v-else>
                                {{
                                  calcBestEffort(
                                    qosBandwidth.qosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ]
                                  )
                                }}
                              </template>
                            </td>
                          </template>
                          <template v-else>
                            <th>最優先</th>
                            <td>
                              <app-select
                                :items="onOff"
                                v-model="
                                  qosBandwidth.qosBandwidthList[
                                    Math.floor(rowNameIndex / 2)
                                  ].isType1TopPriority
                                "
                                item-title="text"
                                item-value="value"
                                hide-details
                              />
                            </td>
                            <td colspan="3"></td>
                          </template>
                        </tr>
                      </template>
                    </tbody>
                  </v-table>
                </template>
              </v-container>
            </div>
            <!--
            関連項目チェック
            No.1
            項目名: QoS方式 CoS/IP Precedence_0～7
            チェック内容: QoS方式が「絶対優先方式」の場合、 CoS/IP Precedence_0～7のうち、
            「High」が1つ以上設定されていること
            -->
            <cross-validation-provider
              :passed="crossValidation1"
              vid="crossValidation1"
              class="my-4"
            >
              CoS/IP PrecedenceはHighを1つ以上設定してください。
            </cross-validation-provider>

            <!--
            関連項目チェック
            No.2
            項目名: QoS方式 CoS/IP Precedence_BE以外
            チェック内容: QoS方式が「帯域制御方式」の場合、 CoS/IP Precedence_BE以外が1つ以上設定されていること
            -->
            <cross-validation-provider
              :passed="crossValidation2"
              vid="crossValidation2"
              class="my-4"
            >
              CoS/IP Precedenceを選択してください。
            </cross-validation-provider>

            <!--
            関連項目チェック
            No.3
            項目名: QoS方式 CoS/IP Precedence_BE以外
            チェック内容: QoS方式が「帯域制御方式」かつ、CoS/IP Precedenceに値が設定されている場合、
            設定済のCoS/IP Precedenceに対応する割当帯域割合が設定されていること パターンA～パターンE全て設定される必要はなく、
            最低限どれか１つ設定されていれば良い。
            -->
            <cross-validation-provider
              :passed="crossValidation3"
              vid="crossValidation3"
              class="my-4"
            >
              CoS/IP Precedenceに対する割当帯域割合を設定してください。
            </cross-validation-provider>

            <!--
            関連項目チェック
            No.4
            項目名: QoS方式 CoS/IP Precedence_BE以外
            チェック内容: QoS方式が「帯域制御方式」かつ、CoS/IP Precedenceに値が設定されていない場合、
            未設定のCoS/IP Precedenceに対応する最優先および割当帯域割合が設定されていないこと
            -->
            <cross-validation-provider
              :passed="crossValidation4"
              vid="crossValidation4"
              class="my-4"
            >
              CoS/IP Precedenceを選択してください。
            </cross-validation-provider>

            <!--
            関連項目チェック
            No.5
            項目名: QoS方式 CoS/IP Precedence_BE以外
            チェック内容: QoS方式が「帯域制御方式」かつ、CoS/IP Precedenceに値が複数設定されている場合、
            全て違う値が設定されていること
            -->
            <cross-validation-provider
              :passed="crossValidation5"
              vid="crossValidation5"
              class="my-4"
            >
              CoS/IP Precedenceが重複しています。
            </cross-validation-provider>

            <!--
            関連項目チェック
            No.6
            項目名: 割当帯域割合
            チェック内容: QoS方式が「帯域制御方式」かつ、割当帯域割合に値が設定されている場合、
            パターンごとの割当帯域割合の合計値が「合計値≦90%」であること
            -->
            <cross-validation-provider
              :passed="crossValidation6"
              vid="crossValidation6"
              class="my-4"
            >
              割当帯域割合はパターン内の合計値が90%以下になるように設定してください。
            </cross-validation-provider>

            <!--
            関連項目チェック
            No.7
            項目名: 最優先
            チェック内容: 最優先が「ON」の場合、割当帯域割合が「設定値 ≧ 5%」かつ「設定値 ＜ 50%」であること
            -->
            <cross-validation-provider
              :passed="crossValidation7"
              vid="crossValidation7"
              class="my-4"
            >
              最優先を「ON」にした場合、割当帯域割合は「5%～45%」の範囲で設定してください。
            </cross-validation-provider>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">設定</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type {
  AbsoluteDefaultPattern,
  PutQosDetail,
  QosBandwidth,
  QosBandwidthItem,
  QosDetail,
} from "@/apis/QosApi";
import QosSettingViewRC from "@/modals/qos/QosSettingViewRC.vue";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";

type PriorityProps =
  | "priority0"
  | "priority1"
  | "priority2"
  | "priority3"
  | "priority4"
  | "priority5"
  | "priority6"
  | "priority7";

export default defineComponent({
  name: "QosSettingEdit",
  props: {
    vpnVnCode: {
      type: String as PropType<string>,
      required: true,
    },
    vnName: {
      type: String as PropType<string>,
      required: false,
    },
    isNew: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    qosDetail: {
      type: Object as PropType<QosDetail>,
      required: true,
    },
  },
  data() {
    return {
      form: {
        qosType: this.qosDetail.qosType,
        priorityTarget: "NONE",
        qosAbsolute: {
          priority0: "HIGH",
          priority1: "HIGH",
          priority2: "HIGH",
          priority3: "HIGH",
          priority4: "HIGH",
          priority5: "HIGH",
          priority6: "HIGH",
          priority7: "HIGH",
        },
      } as PutQosDetail,
      qosAbsolutDefaultPatterns: {} as AbsoluteDefaultPattern,
      qosBandwidth: {
        priorityType1: null,
        priorityType2: null,
        priorityType3: null,
        qosBandwidthList: [],
      } as Omit<QosBandwidth, "qosBandwidthList"> & {
        qosBandwidthList: Omit<QosBandwidthItem, "bestEffort">[];
      },
      absolutePriorityRowNames: [
        { key: "HIGH", value: "High" },
        { key: "MEDIUM", value: "Medium" },
        { key: "NORMAL", value: "Normal" },
        { key: "LOW", value: "Low" },
      ],
      absolutePriorityPropNames: [
        "priority0",
        "priority1",
        "priority2",
        "priority3",
        "priority4",
        "priority5",
        "priority6",
        "priority7",
      ] as PriorityProps[],
      bandwidthRowNames: [
        { key: "PATTERN_A", value: "パターンA" },
        { key: "PATTERN_A", value: "" },
        { key: "PATTERN_B", value: "パターンB" },
        { key: "PATTERN_B", value: "" },
        { key: "PATTERN_C", value: "パターンC" },
        { key: "PATTERN_C", value: "" },
        { key: "PATTERN_D", value: "パターンD" },
        { key: "PATTERN_D", value: "" },
        { key: "PATTERN_E", value: "パターンE" },
        { key: "PATTERN_E", value: "" },
      ],
      priorityTypes: [
        { value: null, text: "" },
        { value: "VALUE_0", text: "0" },
        { value: "VALUE_1", text: "1" },
        { value: "VALUE_2", text: "2" },
        { value: "VALUE_3", text: "3" },
        { value: "VALUE_4TO7", text: "4〜7" },
      ],
      bandwidthValueOptions: [
        { value: null, text: "" },
        { value: 5, text: "5%" },
        { value: 10, text: "10%" },
        { value: 15, text: "15%" },
        { value: 20, text: "20%" },
        { value: 25, text: "25%" },
        { value: 30, text: "30%" },
        { value: 35, text: "35%" },
        { value: 40, text: "40%" },
        { value: 45, text: "45%" },
        { value: 50, text: "50%" },
        { value: 55, text: "55%" },
        { value: 60, text: "60%" },
        { value: 65, text: "65%" },
        { value: 70, text: "70%" },
        { value: 75, text: "75%" },
        { value: 80, text: "80%" },
        { value: 85, text: "85%" },
        { value: 90, text: "90%" },
      ],
      onOff: [
        { value: true, text: "ON" },
        { value: false, text: "OFF" },
      ],
      absolutePattern: "",
      // 初期通信完了フラグ
      isLoaded: false,
    };
  },
  computed: {
    crossValidation1(): boolean {
      return this.form.qosType === "ABSOLUTE" && this.form.qosAbsolute
        ? Object.values(this.form.qosAbsolute).some((value) => value === "HIGH")
        : true;
    },
    crossValidation2(): boolean {
      return this.form.qosType === "BANDWIDTH" &&
        this.qosBandwidth &&
        this.crossValidation4
        ? [
            this.qosBandwidth.priorityType1,
            this.qosBandwidth.priorityType2,
            this.qosBandwidth.priorityType3,
          ].some((value) => value)
        : true;
    },
    crossValidation3(): boolean {
      if (this.form.qosType === "BANDWIDTH" && this.qosBandwidth) {
        // エラーがある時に返す、なかれば、次のチェックに進める
        if (
          this.qosBandwidth.priorityType1 &&
          !this.qosBandwidth.qosBandwidthList
            .map((e) => e.priorityType1Value || 0)
            .some((value) => value > 0)
        ) {
          return false;
        }
        if (
          this.qosBandwidth.priorityType2 &&
          !this.qosBandwidth.qosBandwidthList
            .map((e) => e.priorityType2Value || 0)
            .some((value) => value > 0)
        ) {
          return false;
        }
        if (
          this.qosBandwidth.priorityType3 &&
          !this.qosBandwidth.qosBandwidthList
            .map((e) => e.priorityType3Value || 0)
            .some((value) => value > 0)
        ) {
          return false;
        }
      }
      return true;
    },
    crossValidation4(): boolean {
      if (this.form.qosType === "BANDWIDTH" && this.qosBandwidth) {
        if (!this.qosBandwidth.priorityType1) {
          if (
            this.qosBandwidth.qosBandwidthList
              .map(
                (e) =>
                  e.priorityType1Value ||
                  0 + (e.isType1TopPriority || false ? 1 : 0)
              )
              .reduce((a, c) => a + c) > 0
          )
            return false;
        }
        if (!this.qosBandwidth.priorityType2) {
          if (
            this.qosBandwidth.qosBandwidthList
              .map((e) => e.priorityType2Value || 0)
              .reduce((a, c) => a + c) > 0
          )
            return false;
        }
        if (!this.qosBandwidth.priorityType3) {
          if (
            this.qosBandwidth.qosBandwidthList
              .map((e) => e.priorityType3Value || 0)
              .reduce((a, c) => a + c) > 0
          )
            return false;
        }
      }
      return true;
    },
    crossValidation5(): boolean {
      if (this.form.qosType === "BANDWIDTH" && this.qosBandwidth) {
        if (
          this.qosBandwidth.priorityType1 &&
          this.qosBandwidth.priorityType2 &&
          this.qosBandwidth.priorityType1 === this.qosBandwidth.priorityType2
        ) {
          return false;
        }
        if (
          this.qosBandwidth.priorityType2 &&
          this.qosBandwidth.priorityType3 &&
          this.qosBandwidth.priorityType2 === this.qosBandwidth.priorityType3
        ) {
          return false;
        }
        if (
          this.qosBandwidth.priorityType1 &&
          this.qosBandwidth.priorityType3 &&
          this.qosBandwidth.priorityType1 === this.qosBandwidth.priorityType3
        ) {
          return false;
        }
      }
      return true;
    },
    crossValidation6(): boolean {
      return this.form.qosType === "BANDWIDTH" && this.qosBandwidth
        ? !this.qosBandwidth.qosBandwidthList
            .map(
              (e) =>
                (e.priorityType1Value || 0) +
                (e.priorityType2Value || 0) +
                (e.priorityType3Value || 0)
            )
            .some((value) => 90 < value)
        : true;
    },
    crossValidation7(): boolean {
      return this.form.qosType === "BANDWIDTH" && this.qosBandwidth
        ? !this.qosBandwidth.qosBandwidthList.some(
            (e) =>
              e.isType1TopPriority &&
              ((e.priorityType1Value || 0) < 5 ||
                50 <= (e.priorityType1Value || 0))
          )
        : true;
    },
    qosTypeOptions(): { value: string; text: string }[] {
      if (this.vpnVnCode.startsWith("C")) {
        return [
          { value: "NONE", text: "" },
          { value: "ABSOLUTE", text: "絶対優先方式" },
        ];
      } else {
        return [
          { value: "NONE", text: "" },
          { value: "ABSOLUTE", text: "絶対優先方式" },
          { value: "BANDWIDTH", text: "帯域制御方式" },
        ];
      }
    },
    priorityTargetOptions(): { value: string; text: string }[] {
      if (this.vpnVnCode.startsWith("C")) {
        return [
          { value: "NONE", text: "" },
          { value: "COS", text: "CoS" },
          { value: "IPPRECEDENCE", text: "IP Precedence" },
        ];
      } else {
        return [
          { value: "NONE", text: "" },
          { value: "IPPRECEDENCE", text: "IP Precedence" },
        ];
      }
    },
    absolutePatternOption(): { value: string | null; text: string }[] {
      if (this.vpnVnCode.startsWith("C")) {
        return [
          { value: null, text: "" },
          { value: "A", text: "パターンA" },
          { value: "B", text: "パターンB" },
        ];
      } else {
        return [
          { value: null, text: "" },
          { value: "A", text: "パターンA" },
        ];
      }
    },
    changeAbsolutePriorities() {
      return `${this.form.priorityTarget}-${this.absolutePattern}`;
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  watch: {
    changeAbsolutePriorities: function () {
      if (this.form.qosType === "ABSOLUTE" && this.form.qosAbsolute) {
        if (this.form.priorityTarget === "IPPRECEDENCE") {
          this.form.qosAbsolute.priority6 = "HIGH";
        }
        if (this.absolutePattern === "A") {
          if (
            this.form.priorityTarget === "IPPRECEDENCE" ||
            this.form.priorityTarget === "COS"
          ) {
            this.form.qosAbsolute = {
              ...this.qosAbsolutDefaultPatterns.patternA,
            };
          }
        }
        if (this.absolutePattern === "B") {
          if (this.form.priorityTarget === "IPPRECEDENCE") {
            // L2VN面のときのみここのコードが実行される
            // 特記事項
            // IP Precedence_6はHigh固定で、変更不可
            const priority6 = this.form.qosAbsolute.priority6;
            this.form.qosAbsolute = {
              ...this.qosAbsolutDefaultPatterns.patternBIp,
            };
            this.form.qosAbsolute.priority6 = priority6;
          } else if (this.form.priorityTarget === "COS") {
            this.form.qosAbsolute = {
              ...this.qosAbsolutDefaultPatterns.patternBCos,
            };
          }
        }
      }
    },
  },
  methods: {
    async load() {
      this.qosAbsolutDefaultPatterns =
        await this.$api.qosApi.getAbsoluteDefaultPatterns();

      this.form.qosKeyId = this.qosDetail.qosKeyId;
      this.form.qosType = this.qosDetail.qosType;
      this.form.priorityTarget = this.qosDetail.priorityTarget;

      this.qosBandwidth = {
        priorityType1: null,
        priorityType2: null,
        priorityType3: null,
        qosBandwidthList: [],
      };
      ["PATTERN_A", "PATTERN_B", "PATTERN_C", "PATTERN_D", "PATTERN_E"].forEach(
        (pattern) => {
          this.qosBandwidth.qosBandwidthList.push({
            patternName: pattern as
              | "PATTERN_A"
              | "PATTERN_B"
              | "PATTERN_C"
              | "PATTERN_D"
              | "PATTERN_E",
            priorityType1Value: null,
            priorityType2Value: null,
            priorityType3Value: null,
            isType1TopPriority: false,
          });
        }
      );

      if (this.form.qosType === "ABSOLUTE" && this.qosDetail.qosAbsolute) {
        this.form.qosAbsolute = {
          ...this.qosDetail.qosAbsolute,
        };
      }
      if (this.form.qosType === "BANDWIDTH" && this.qosDetail.qosBandwidth) {
        this.qosBandwidth = {
          priorityType1: this.qosDetail.qosBandwidth?.priorityType1,
          priorityType2: this.qosDetail.qosBandwidth?.priorityType2,
          priorityType3: this.qosDetail.qosBandwidth?.priorityType3,
          qosBandwidthList: [],
        };
        [
          "PATTERN_A",
          "PATTERN_B",
          "PATTERN_C",
          "PATTERN_D",
          "PATTERN_E",
        ].forEach((pattern) => {
          const item = this.qosDetail.qosBandwidth?.qosBandwidthList?.find(
            (e) => e.patternName === pattern
          );
          if (item) {
            this.qosBandwidth.qosBandwidthList.push({
              patternName: item.patternName,
              priorityType1Value: item.priorityType1Value,
              priorityType2Value: item.priorityType2Value,
              priorityType3Value: item.priorityType3Value,
              isType1TopPriority: item.isType1TopPriority,
            });
          } else {
            this.qosBandwidth.qosBandwidthList.push({
              patternName: pattern as
                | "PATTERN_A"
                | "PATTERN_B"
                | "PATTERN_C"
                | "PATTERN_D"
                | "PATTERN_E",
              priorityType1Value: null,
              priorityType2Value: null,
              priorityType3Value: null,
              isType1TopPriority: false,
            });
          }
        });
      }
    },
    /**
     * 帯域制御方式の優先タイプ値の合計を算出
     * @param item QoS帯域設定地情報
     */
    sumPriorityTypeValue(item: Omit<QosBandwidthItem, "bestEffort">): number {
      return (
        (item.priorityType1Value || 0) +
        (item.priorityType2Value || 0) +
        (item.priorityType3Value || 0)
      );
    },
    /**
     * 帯域制御方式のベストエフォートを算出
     * @param item QoS帯域設定地情報
     */
    calcBestEffort(item: Omit<QosBandwidthItem, "bestEffort">): string {
      return (item.priorityType1Value || 0) +
        (item.priorityType2Value || 0) +
        (item.priorityType3Value || 0) >
        0
        ? `${
            100 -
            ((item.priorityType1Value || 0) +
              (item.priorityType2Value || 0) +
              (item.priorityType3Value || 0))
          }%`
        : "-";
    },
    async submit() {
      const putItem: PutQosDetail = cloneDeep(this.form);

      if (putItem.qosType === "ABSOLUTE") {
        putItem.qosBandwidth = null;
      }

      if (putItem.qosType === "BANDWIDTH") {
        putItem.qosAbsolute = null;
        putItem.qosBandwidth = { ...this.qosBandwidth };
      }

      await this.$modal.show(QosSettingViewRC, {
        mode: "C",
        isNew: this.isNew,
        qosDetail: putItem,
      });

      await this.$api.qosApi.putQosDetail(this.vpnVnCode, putItem);

      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>

<style lang="scss" scoped>
.radio-button {
  margin: 0 auto;
}
.v-table__wrapper > table > thead > tr > th {
  border: 1px solid #dee2e6;
}
.v-table__wrapper > table > tbody > tr:not(:last-child) > th {
  border: 1px solid #dee2e6;
}
.table {
  border: 1px solid #dee2e6;
  th {
    background-color: #e9ecef;
    font-weight: bold !important;
    padding: 4px !important;
  }
}
.table-head-select {
  :deep(.v-field__overlay) {
    background-color: white;
  }
}
:deep(td) {
  padding: 4px !important;
}
.fixed-width {
  width: 107px;
}
</style>
